import { NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  NbAccordionModule,
  NbButtonModule,
  NbCardModule,
  NbDateService,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux';
import { RobotFamilyMetadata } from 'src/app/models/robot-family-metadata';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import {
  DeleteMetadata,
  DeleteMetadataSuccess,
  PostMetadata,
  SaveMetadataComplete,
  UpdateMetadata,
  UpdateMetadataSuccess
} from '../../redux';

@Component({
  selector: 'app-edit-robot-family-metadata',
  templateUrl: './edit-robot-family-metadata.component.html',
  styleUrls: ['./edit-robot-family-metadata.component.css', '../../../../shared/common_syles.css'],
  imports: [
    NbCardModule,
    NgIf,
    NbIconModule,
    NbButtonModule,
    FormsModule,
    ReactiveFormsModule,
    NbInputModule,
    NbSelectModule,
    NgFor,
    NbOptionModule,
    NbAccordionModule,
    NbTooltipModule
  ],
  standalone: true
})
export class EditRobotFamilyMetadataComponent implements OnInit, AfterViewInit {
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  public robotFamilyMetadata: RobotFamilyMetadata;
  public allRFM: [RobotFamilyMetadata];
  public action: string;
  public username = '';
  public metadataForm: UntypedFormGroup;
  public metadataTypes = ['cbo'];
  public extentTypes = [
    'around_bed',
    'around_sofa',
    'around_coffee_table',
    'around_dining_table',
    'around_toilet',
    'around_kitchen_island',
    'rug',
    'front_of_kitchen_counter',
    'front_of_refrigerator',
    'around_christmas_tree',
    'around_pet_bowl',
    'around_litter_box',
    'front_of_oven',
    'front_of_dishwasher'
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    protected ref: NbDialogRef<EditRobotFamilyMetadataComponent>,
    private action$: Actions,
    private dialogService: NbDialogService,
    protected dateService: NbDateService<Date>,
    private toastrService: NbToastrService
  ) {
    this.metadataForm = this.fb.group(
      {
        robot_family: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9\:_-]*$/i)]],
        metadata_type: ['', Validators.required],
        metadata: this.fb.array([])
      },
      { validators: [RobotFamilyMetadataValidator.validate_metadata()] }
    );
  }
  public metadata(): UntypedFormArray {
    return this.metadataForm.get('metadata') as UntypedFormArray;
  }

  public ngOnInit(): void {
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    if (this.action === ACTIONS.EDIT) {
      this.action$.pipe(ofActionCompleted(UpdateMetadataSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
      this.action$.pipe(ofActionCompleted(DeleteMetadataSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
    } else {
      this.action$.pipe(ofActionCompleted(SaveMetadataComplete), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
    }

    if (this.action === ACTIONS.EDIT || this.action === ACTIONS.CLONE) {
      this.clearFormArray();
      this.robotFamilyMetadata.metadata.forEach((m) => {
        const metadata: UntypedFormGroup = this.newMetadata();
        this.metadata().push(metadata);
      });
      this.metadataForm.patchValue(this.robotFamilyMetadata);
    }
  }

  public ngAfterViewInit(): void {
    this.metadataForm.addValidators(
      RobotFamilyMetadataValidator.validate_key(this.allRFM, this.robotFamilyMetadata, this.action)
    );
    this.metadataForm.updateValueAndValidity();
  }

  public newMetadata(): UntypedFormGroup {
    return this.fb.group({
      extent_type: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9\:_-]*$/i)]],
      confidence: [0],
      priority: null
    });
  }

  public clearFormArray() {
    this.metadata().clear();
  }

  public addMetadata() {
    this.metadata().push(this.newMetadata());
  }

  public removeMetadata(qi: number) {
    this.metadata().removeAt(qi);
  }

  public save() {
    this.metadataForm.markAllAsTouched();
    if (this.metadataForm.valid) {
      const robot_family = this.metadataForm.get('robot_family').value;
      const metadata_type = this.metadataForm.get('metadata_type').value;
      const metadata = this.metadataForm.get('metadata').value;
      if (this.action === ACTIONS.EDIT) {
        this.robotFamilyMetadata.metadata = metadata;
        this.robotFamilyMetadata.last_updated_by = this.username;
        if (
          this.robotFamilyMetadata.robot_family !== robot_family ||
          this.robotFamilyMetadata.metadata_type !== metadata_type
        ) {
          const metadataToDelete = RobotFamilyMetadata.copy(this.robotFamilyMetadata);
          metadataToDelete.deleted = true;
          this.store.dispatch(new DeleteMetadata(metadataToDelete));
          this.robotFamilyMetadata.robot_family = robot_family;
          this.robotFamilyMetadata.metadata_type = metadata_type;
          this.store.dispatch(new PostMetadata(this.robotFamilyMetadata));
        } else {
          this.robotFamilyMetadata.metadata = metadata;
          this.store.dispatch(new UpdateMetadata(this.robotFamilyMetadata));
        }
      } else {
        this.robotFamilyMetadata.created_by = this.username;
        this.robotFamilyMetadata.last_updated_by = this.username;
        this.robotFamilyMetadata.robot_family = robot_family;
        this.robotFamilyMetadata.metadata_type = metadata_type;
        this.robotFamilyMetadata.metadata = metadata;
        this.store.dispatch(new PostMetadata(this.robotFamilyMetadata));
      }
    } else {
    }
  }

  public close() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Confirm Close`,
          message: $localize`Are you sure that you want to close the window? Any changes that you have made will be lost.`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
  }
}

export class RobotFamilyMetadataValidator {
  public static validate_metadata(): ValidatorFn {
    return (form: UntypedFormGroup): ValidationErrors | null => {
      const md = form.get('metadata').value;

      if (md.length <= 0) {
        return { validate_metadata_exist: true };
      }

      const filtered_md = md.filter((m) => m.extent_type !== '');

      if (filtered_md.length > 0) {
        const duplicates = new Set();
        const isDuplicate = filtered_md.some((obj) => duplicates.size === duplicates.add(obj.extent_type).size);

        return isDuplicate ? { validate_metadata: true } : null;
      }

      return null;
    };
  }

  public static validate_key(allRFM: any, loadedMetadata: RobotFamilyMetadata, title: string): ValidatorFn {
    return (form: UntypedFormGroup): ValidationErrors | null => {
      const rf = form.get('robot_family').value;
      const mt = form.get('metadata_type').value;
      if (mt !== '' && rf !== '' && allRFM.length > 0) {
        if (title !== ACTIONS.EDIT) {
          const check = allRFM.find((m) => m.robot_family === rf && m.metadata_type === mt) === undefined;

          if (!check) {
            return { validate_key: true };
          }
        } else if (loadedMetadata.robot_family !== rf) {
          return allRFM.find((m) => m.robot_family === rf && m.metadata_type === mt) === undefined
            ? null
            : { validate_key: true };
        }
      }

      return null;
    };
  }
}
